import React, { useEffect, useState } from 'react';
import { apiService } from '../services/api';
import { OpeningHours, OpeningHoursStatus } from '../types';
import { addDaysLondon, formatLondon, londonDateKey } from '../shared/dateUtils';
import './WorkingHoursInfo.css';

const WorkingHoursInfo: React.FC = () => {
    const [hours, setHours] = useState<OpeningHours | null>(null);
    const [status, setStatus] = useState<OpeningHoursStatus | null>(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);

    useEffect(() => {
        const fetchData = async () => {
            try {
                const [hoursData, statusData] = await Promise.all([
                    apiService.getOpeningHours(),
                    fetch('/api/opening-hours/status').then(res => res.json() as Promise<OpeningHoursStatus>)
                ]);
                setHours(hoursData);
                setStatus(statusData);
            } catch (err) {
                setError('Unable to load working hours');
                console.error('Error fetching opening hours:', err);
            } finally {
                setLoading(false);
            }
        };

        fetchData();
    }, []);

    const formatNextOpeningTime = (nextOpening: OpeningHoursStatus['nextOpeningTime']) => {
        if (!nextOpening) return null;

        // `nextOpening.date` is a London date key `YYYY-MM-DD` (or an ISO
        // string — `.split('T')[0]` handles both safely).
        const openingDateKey = nextOpening.date.split('T')[0];
        const todayKey = londonDateKey();
        const tomorrowKey = addDaysLondon(todayKey, 1);

        let dayText = '';
        if (openingDateKey === todayKey) {
            dayText = 'today';
        } else if (openingDateKey === tomorrowKey) {
            dayText = 'tomorrow';
        } else {
            // Parse the date key as a London-local calendar day for display.
            const [y, m, d] = openingDateKey.split('-').map(Number);
            const asDate = new Date(Date.UTC(y, m - 1, d, 12, 0, 0));
            dayText = `on ${formatLondon(asDate, {
                weekday: 'long',
                day: 'numeric',
                month: 'long'
            })}`;
        }

        return `${dayText} at ${nextOpening.time}`;
    };

    if (loading) {
        return <div className="working-hours-content">Loading...</div>;
    }

    if (error || !hours) {
        return <div className="working-hours-content error">{error || 'Hours not available'}</div>;
    }

    const daysOfWeek = [
        'monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday', 'sunday'
    ] as const;

    const todayKey = londonDateKey();

    const upcomingExceptions = (hours.exceptions || [])
        .filter(exception => exception.date >= todayKey)
        .sort((a, b) => (a.date < b.date ? -1 : a.date > b.date ? 1 : 0));

    const hasUpcomingExceptions = upcomingExceptions.length > 0;

    return (
        <div className="working-hours-content">
            {/* Status Section */}
            {status && (
                <div className={`hours-status-indicator ${status.isOpen ? 'open' : 'closed'}`}>
                    <div className="status-icon">
                        {status.isOpen ? '🟢' : '🔴'}
                    </div>
                    <div className="status-text-section">
                        <h4>{status.isOpen ? "We're Open!" : "We're Closed"}</h4>
                        {!status.isOpen && (
                            <p className="status-subtitle">
                                But you can still order and plan your dinner.
                            </p>
                        )}
                    </div>
                </div>
            )}

            {/* Next Opening Section */}
            {status && !status.isOpen && status.nextOpeningTime && (
                <div className="next-opening-section">
                    <h5>NEXT OPENING:</h5>
                    <p className="next-opening-time">
                        {formatNextOpeningTime(status.nextOpeningTime)}
                    </p>
                </div>
            )}

            {hasUpcomingExceptions && (
                <div className="hours-exceptions">
                    <h3>Special Hours</h3>
                    {upcomingExceptions.map((exception, index) => (
                        <div key={index} className="exception-row">
                            <span className="exception-date">{exception.date}</span>
                            <span className="exception-hours">
                                {!exception.isOpen || exception.timeSlots.length === 0
                                    ? 'Closed'
                                    : exception.timeSlots.map(slot => `${slot.start} - ${slot.end}`).join(', ')
                                }
                            </span>
                            {exception.reason && (
                                <span className="exception-reason">({exception.reason})</span>
                            )}
                        </div>
                    ))}
                </div>
            )}

            {/* Weekly Schedule */}
            <div className="hours-schedule">
                {hasUpcomingExceptions && <h3>Regular Hours</h3>}
                {daysOfWeek
                    .map(day => {
                        const daySchedule = hours.weeklySchedule.find(d => d.dayOfWeek === day);
                        const isClosed = !daySchedule || !daySchedule.isOpen || daySchedule.timeSlots.length === 0;

                        return {
                            day,
                            daySchedule,
                            isClosed
                        };
                    })
                    .filter(({ isClosed }) => !isClosed) // Only show open days
                    .map(({ day, daySchedule }) => (
                        <div key={day} className="hours-row">
                            <span className="day-name">
                                {day.charAt(0).toUpperCase() + day.slice(1)}
                            </span>
                            <span className="day-hours">
                                {daySchedule!.timeSlots.map(slot => `${slot.start} - ${slot.end}`).join(', ')}
                            </span>
                        </div>
                    ))
                }
            </div>


        </div>
    );
};

export default WorkingHoursInfo;
